<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import { formatWhatsapp } from 'dashboard/helper/whatsappMask';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const agentTeams = ref({});

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const agentGroups = computed(() => {
  const customerAgents = agentList.value.filter(
    agent => !agent.platform_member?.active
  );
  const platformMembers = agentList.value.filter(
    agent => agent.platform_member?.active
  );

  return [
    {
      id: 'customer',
      label: t('AGENT_MGMT.LIST.GROUPS.CUSTOMER_AGENTS'),
      agents: customerAgents,
    },
    {
      id: 'platform',
      label: t('AGENT_MGMT.LIST.GROUPS.PLATFORM_MEMBERS'),
      agents: platformMembers,
    },
  ].filter(group => group.agents.length);
});
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');
const allTeams = computed(() => getters['teams/getTeams'].value);

// Verifica se o usuário tem apenas permissão agents_access (não pode criar/deletar agentes)
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const { accountId } = useAccount();

const hasLimitedAgentsAccess = computed(() => {
  // Administrators sempre têm acesso completo
  if (currentRole.value === 'administrator') {
    return false;
  }

  const permissions = getUserPermissions(currentUser.value, accountId.value);
  // Usuário tem permissão agents_access mas não é administrator
  return permissions.includes('agents_access');
});

// Sidebar dispara este evento via createSidebarActionHandler.new-agent
// (item "Agentes" no menu Settings). Permissao ja foi checada na sidebar
// (requiredPermissions), mas reforco aqui por seguranca.
const handleSidebarAgentNew = () => {
  if (hasLimitedAgentsAccess.value) return;
  showAddPopup.value = true;
};

onMounted(async () => {
  emitter.on(BUS_EVENTS.SIDEBAR_OPEN_AGENT_NEW, handleSidebarAgentNew);
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('customRole/getCustomRole'),
    store.dispatch('teams/get'),
  ]);

  // Carrega os times de cada agente
  await loadAgentTeams();
});

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.SIDEBAR_OPEN_AGENT_NEW, handleSidebarAgentNew);
});

const loadAgentTeams = async () => {
  const teamsMap = {};

  try {
    // Para cada time, carrega os membros
    for (const team of allTeams.value) {
      try {
        await store.dispatch('teamMembers/get', { teamId: team.id });
        const members = getters['teamMembers/getTeamMembers'].value(team.id);

        // Mapeia user_id -> team_name
        const uniqueUserIds = [...new Set((members || []).map(m => m.id))];
        uniqueUserIds.forEach(userId => {
          if (!teamsMap[userId]) {
            teamsMap[userId] = [];
          }
          teamsMap[userId].push(team.name);
        });
      } catch (error) {
        console.warn(`Erro ao carregar membros do time ${team.id}:`, error);
      }
    }

    agentTeams.value = teamsMap;
  } catch (error) {
    console.error('Erro ao carregar times dos agentes:', error);
  }
};

const getAgentTeams = agentId => {
  return agentTeams.value[agentId] || [];
};

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const departmentLabels = computed(() => ({
  management: t('AGENT_MGMT.LIST.DEPARTMENTS.MANAGEMENT'),
  consulting: t('AGENT_MGMT.LIST.DEPARTMENTS.CONSULTING'),
  development: t('AGENT_MGMT.LIST.DEPARTMENTS.DEVELOPMENT'),
  support: t('AGENT_MGMT.LIST.DEPARTMENTS.SUPPORT'),
}));

const getPlatformMemberLabel = agent => {
  const { department, organization } = agent.platform_member;

  return `${organization.name} - ${departmentLabels.value[department]}`;
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  // Usuários com acesso limitado não podem deletar agentes
  if (hasLimitedAgentsAccess.value) {
    return false;
  }

  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-users"
        accent="blue"
        :title="$t('AGENT_MGMT.HEADER')"
        :subtitle="$t('AGENT_MGMT.DESCRIPTION')"
      />
    </template>
    <woot-loading-state
      v-if="uiFlags.isFetching"
      :message="$t('AGENT_MGMT.LOADING')"
    />
    <p
      v-else-if="!agentList.length"
      class="flex flex-col items-center justify-center h-full text-base text-n-slate-12 p-8"
    >
      {{ $t('AGENT_MGMT.LIST.404') }}
    </p>
    <table v-else class="divide-y divide-n-weak">
      <thead class="text-left">
        <tr class="text-n-slate-11 text-sm">
          <th class="py-3 font-semibold">
            {{ $t('AGENT_MGMT.LIST.TABLE_HEADER.NAME') || 'Agente' }}
          </th>
          <th class="py-3 font-semibold">
            {{ $t('AGENT_MGMT.LIST.TABLE_HEADER.ROLE') || 'Função' }}
          </th>
          <th class="py-3 font-semibold">
            {{ $t('AGENT_MGMT.LIST.TABLE_HEADER.STATUS') || 'Status' }}
          </th>
          <th class="py-3 font-semibold">
            {{ $t('AGENT_MGMT.LIST.TABLE_HEADER.TEAM') || 'Time' }}
          </th>
          <th class="py-3 font-semibold text-right">
            {{ $t('AGENT_MGMT.LIST.TABLE_HEADER.ACTIONS') || 'Ações' }}
          </th>
        </tr>
      </thead>
      <tbody
        v-for="group in agentGroups"
        :key="group.id"
        class="divide-y divide-n-weak text-n-slate-11"
      >
        <tr class="bg-n-slate-2">
          <td colspan="5" class="px-3 py-2 text-xs font-semibold uppercase">
            {{
              $t('AGENT_MGMT.LIST.GROUPS.LABEL_WITH_COUNT', {
                label: group.label,
                count: group.agents.length,
              })
            }}
          </td>
        </tr>
        <tr v-for="agent in group.agents" :key="agent.email">
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <div class="flex flex-row items-center gap-4">
              <Avatar
                :src="agent.thumbnail"
                :name="agent.name"
                :status="agent.availability_status"
                :size="40"
                hide-offline-status
                rounded-full
              />
              <div>
                <span class="block font-medium capitalize">
                  {{ agent.name }}
                </span>
                <span class="block">{{ agent.email }}</span>
                <span v-if="agent.whatsapp" class="block text-n-slate-11">
                  {{ formatWhatsapp(agent.whatsapp) }}
                </span>
                <span
                  v-if="agent.platform_member?.active"
                  class="inline-flex mt-1 px-2 py-0.5 text-xs font-medium bg-n-iris-2 text-n-iris-11 rounded-md border border-n-iris-6"
                >
                  {{ getPlatformMemberLabel(agent) }}
                </span>
              </div>
            </div>
          </td>

          <td class="relative py-4 ltr:pr-4 rtl:pl-4">
            <span
              class="block font-medium w-fit"
              :class="{
                'hover:text-gray-900 group cursor-pointer':
                  agent.custom_role_id,
              }"
            >
              {{ getAgentRoleName(agent) }}

              <div
                class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-n-weak shadow-lg top-14 md:top-12 dark:bg-n-solid-2"
                :class="{ 'group-hover:block': agent.custom_role_id }"
              >
                <div class="flex flex-col gap-1 p-4">
                  <span class="font-semibold">
                    {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                  </span>
                  <ul class="pl-4 mb-0 list-disc">
                    <li
                      v-for="permission in getAgentRolePermissions(agent)"
                      :key="permission"
                      class="font-normal"
                    >
                      {{
                        $t(
                          `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                        )
                      }}
                    </li>
                  </ul>
                </div>
              </div>
            </span>
          </td>

          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <span v-if="agent.confirmed">
              {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
            </span>
            <span v-if="!agent.confirmed">
              {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
            </span>
          </td>

          <!-- Coluna de Times -->
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <div
              v-if="getAgentTeams(agent.id).length > 0"
              class="flex flex-wrap gap-1"
            >
              <span
                v-for="teamName in getAgentTeams(agent.id)"
                :key="teamName"
                class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-n-iris-2 text-n-iris-11 rounded-md border border-n-iris-6"
              >
                <Icon icon="i-lucide-users" class="size-3" />
                {{ teamName }}
              </span>
            </div>
            <span v-else class="text-n-slate-9 text-sm"> Sem time </span>
          </td>

          <td class="py-4">
            <div class="flex justify-end gap-1">
              <Button
                v-if="showEditAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                @click="openEditPopup(agent)"
              />
              <Button
                v-if="showDeleteAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[agent.id]"
                @click="openDeletePopup(agent)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        :is-human="currentAgent.is_human"
        :whatsapp="currentAgent.whatsapp || ''"
        :has-limited-access="hasLimitedAgentsAccess"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </PageLayout>
</template>
